/**
 * A hotel's logo, drawn whole and legible on whatever it sits on.
 *
 * A logo file is made for one kind of background, and ours go on both: the
 * rail is dark in every theme, the sign-in page follows the theme, and the
 * guest tablet is always light. A white wordmark on a transparent ground
 * vanishes on the tablet; a navy one vanishes on the rail. So the image is
 * sampled once — its opaque pixels' mean luminance — and, only where it would
 * disappear, it is changed. A logo with its own background, or one mid-toned
 * enough to read on either, is left alone.
 *
 * A light logo on a light ground is redrawn in ink (--logo-ink): its white
 * and grey become the ink and any colour in it stays, which is how a hotel's
 * own letterhead prints the logo it made for dark backgrounds. It used to be
 * set on a dark plate, and on the white tablet that plate read as a black
 * sticker above the welcome. The plate is still the fallback, for a light
 * logo that is pale colour rather than white, and still what a dark logo gets
 * on a dark ground.
 */

import { useEffect, useState, useSyncExternalStore } from 'react'
import { cn } from '@/lib/utils'

/** What the logo is sitting on. `theme` follows the light/dark choice. */
export type Surface = 'dark' | 'light' | 'theme'

/**
 * `light`/`dark`: transparent, and too pale/too dark for the opposite ground.
 * `opaque`: brings its own background. `mixed`: reads on both.
 */
export type LogoTone = 'light' | 'dark' | 'mixed' | 'opaque' | 'unknown'

// Calibrated on real logos: a white wordmark measures 1.0, a grey-and-orange
// one 0.39 (legible on both), a navy one under 0.1.
const LIGHT_ABOVE = 0.6
const DARK_BELOW = 0.12

// Chroma, as a fraction of full scale, between a logo's neutrals (its white,
// and the grey of its anti-aliased edges) and its colours. In between, a pixel
// is part ink and part its own colour, so the edge of an accent stays smooth.
const NEUTRAL_BELOW = 0.12
const COLOURED_ABOVE = 0.3

// The widest the inked copy is drawn. The largest logo on screen is 18rem, so
// this is sharp at 3x; a larger upload is scaled down, never up.
const INK_MAX_WIDTH = 1200

const images = new Map<string, Promise<HTMLImageElement | null>>()
const tones = new Map<string, Promise<LogoTone>>()
const inks = new Map<string, Promise<string | null>>()

function channel(value: number): number {
  const c = value / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** Classify decoded pixels. Exported for the tests; jsdom has no canvas. */
export function classify(data: Uint8ClampedArray): LogoTone {
  let opaque = 0
  let sum = 0
  const total = data.length / 4
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue
    opaque += 1
    sum += 0.2126 * channel(data[i]) + 0.7152 * channel(data[i + 1]) + 0.0722 * channel(data[i + 2])
  }
  if (!opaque) return 'unknown'
  if (opaque / total > 0.95) return 'opaque'
  const mean = sum / opaque
  return mean > LIGHT_ABOVE ? 'light' : mean < DARK_BELOW ? 'dark' : 'mixed'
}

/** The plate a logo of this tone needs on this ground, if any. */
export function plateFor(tone: LogoTone | null, onDark: boolean): 'dark' | 'light' | null {
  if (tone === 'light' && !onDark) return 'dark'
  if (tone === 'dark' && onDark) return 'light'
  return null
}

/**
 * Redraw a logo's neutral pixels in `ink`, in place, keeping every alpha and
 * every colour. Exported for the tests.
 */
export function inkNeutrals(data: Uint8ClampedArray, ink: readonly [number, number, number]): void {
  for (let i = 0; i < data.length; i += 4) {
    if (!data[i + 3]) continue
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]]
    const chroma = (Math.max(r, g, b) - Math.min(r, g, b)) / 255
    const share = (chroma - NEUTRAL_BELOW) / (COLOURED_ABOVE - NEUTRAL_BELOW)
    const own = Math.min(1, Math.max(0, share))
    data[i] = ink[0] + (r - ink[0]) * own
    data[i + 1] = ink[1] + (g - ink[1]) * own
    data[i + 2] = ink[2] + (b - ink[2]) * own
  }
}

function load(src: string): Promise<HTMLImageElement | null> {
  let pending = images.get(src)
  if (!pending) {
    pending = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image.naturalWidth ? image : null)
      image.onerror = () => resolve(null)
      image.src = src
    })
    images.set(src, pending)
  }
  return pending
}

/** The image drawn `width` wide on a canvas, or null where it cannot be read. */
function draw(image: HTMLImageElement, width: number) {
  const height = Math.max(1, Math.round((image.naturalHeight / image.naturalWidth) * width))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.drawImage(image, 0, 0, width, height)
  return { canvas, context, pixels: context.getImageData(0, 0, width, height) }
}

function measure(src: string): Promise<LogoTone> {
  let pending = tones.get(src)
  if (!pending) {
    pending = load(src).then((image) => {
      if (!image) return 'unknown'
      try {
        const drawn = draw(image, 48)
        return drawn ? classify(drawn.pixels.data) : 'unknown'
      } catch {
        // A tainted canvas or no canvas at all: draw the logo as it is.
        return 'unknown'
      }
    })
    tones.set(src, pending)
  }
  return pending
}

/** --logo-ink as RGB, resolved by the canvas so any CSS colour syntax works. */
function inkColour(): [number, number, number] | null {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--logo-ink').trim()
  if (!value) return null
  const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.fillStyle = value
  context.fillRect(0, 0, 1, 1)
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data
  return [r, g, b]
}

/**
 * The logo redrawn in ink, as an image address, or null when that would not
 * make it readable either — a logo that is pale colour, not white — and it
 * should go on a plate after all.
 */
function inked(src: string): Promise<string | null> {
  let pending = inks.get(src)
  if (!pending) {
    pending = load(src).then((image) => {
      const ink = inkColour()
      if (!image || !ink) return null
      try {
        const drawn = draw(image, Math.min(image.naturalWidth, INK_MAX_WIDTH))
        if (!drawn) return null
        inkNeutrals(drawn.pixels.data, ink)
        if (classify(drawn.pixels.data) === 'light') return null
        drawn.context.putImageData(drawn.pixels, 0, 0)
        return drawn.canvas.toDataURL('image/png')
      } catch {
        return null
      }
    })
    inks.set(src, pending)
  }
  return pending
}

/** `get(src)` once `wanted`, forgetting a result that was for another `src`. */
function useResult<T>(src: string, wanted: boolean, get: (src: string) => Promise<T>) {
  const [result, setResult] = useState<{ src: string; value: T } | null>(null)
  useEffect(() => {
    if (!wanted) return
    let live = true
    void get(src).then((value) => {
      if (live) setResult({ src, value })
    })
    return () => {
      live = false
    }
  }, [src, wanted, get])
  return result?.src === src ? { value: result.value } : null
}

function themeIsDark(): boolean {
  const chosen = document.documentElement.getAttribute('data-theme')
  if (chosen) return chosen === 'dark'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function subscribeTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  media?.addEventListener('change', onChange)
  return () => {
    observer.disconnect()
    media?.removeEventListener('change', onChange)
  }
}

export function HotelLogo({
  src,
  surface,
  alt = '',
  className,
}: {
  src: string
  surface: Surface
  /** Empty when the hotel's name is written beside it, which it usually is. */
  alt?: string
  /** Sizes the image: a height, and a width limit. */
  className?: string
}) {
  const tone = useResult(src, true, measure)?.value ?? null
  const dark = useSyncExternalStore(subscribeTheme, themeIsDark, () => true)
  const onDark = surface === 'dark' || (surface === 'theme' && dark)

  const plate = plateFor(tone, onDark)
  // A light logo on a light ground: redraw it in ink rather than plate it.
  const ink = useResult(src, plate === 'dark', inked)

  const image = (address: string) => (
    <img
      src={address}
      alt={alt}
      className={cn('object-contain object-left rtl:object-right', className)}
    />
  )
  if (!plate) return image(src)
  if (plate === 'dark') {
    // Still being redrawn: the original, which is as invisible as nothing
    // for these few milliseconds, holds the layout without flashing a plate.
    if (!ink) return image(src)
    if (ink.value) return image(ink.value)
  }
  return (
    <span
      className={cn(
        'inline-flex max-w-full shrink-0 rounded-[10px] px-2.5 py-1.5',
        plate === 'dark' ? 'bg-[var(--rail)]' : 'bg-white',
      )}
    >
      {image(src)}
    </span>
  )
}
